import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse } from '@packages/global';
import i18n from '../i18n';

const ROUTING_ERROR_KEYS: Record<string, string> = {
  MODEL_ROUTE_UNAVAILABLE: 'common_ui:routingErrors.routeUnavailable',
  MODEL_KEY_WAIT_TIMEOUT: 'common_ui:routingErrors.keyWaitTimeout',
};

/**
 * HTTP 客户端
 * 提供统一的 Axios 实例和拦截器配置
 */
class HttpClient {
  public readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: '/api',
      timeout: 60000,
    });

    this.setupInterceptors();
  }

  /**
   * 配置请求和响应拦截器
   */
  private setupInterceptors(): void {
    // 请求拦截器 - 自动添加 token 和语言偏好
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // 从 Zustand persist 中读取用户语言偏好
        try {
          const stored = localStorage.getItem('language-storage');
          if (stored) {
            const parsed = JSON.parse(stored);
            const locale = parsed?.state?.locale;
            if (locale) {
              config.headers['Accept-Language'] = locale;
            }
          }
        } catch {
          // 忽略解析错误
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器 - 统一错误处理
    this.instance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      (error) => {
        const responseData = error.response?.data;
        const translationKey = ROUTING_ERROR_KEYS[responseData?.code];
        if (translationKey) {
          const message = i18n.t(translationKey);
          error.message = message;
          if (responseData && typeof responseData === 'object') {
            responseData.message = message;
            responseData.error = message;
          }
        }
        // 401 未授权 - 清除 token 并跳转到登录页
        // 排除登录/注册接口本身，避免跨租户登录失败时触发跳转
        const requestUrl = error.config?.url ?? '';
        const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');
        if (error.response?.status === 401 && !isAuthEndpoint) {
          localStorage.removeItem('token');
          // 动态导入 store 避免循环依赖
          import('../stores/authStore').then(({ useAuthStore }) => {
            useAuthStore.getState().logout();
          });
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * GET 请求
   */
  async get<T>(url: string, params?: any, options?: { timeout?: number }): Promise<T> {
    const response = await this.instance.get<ApiResponse<T>>(url, {
      params,
      ...options
    });
    return response.data.data!;
  }

  /**
   * POST 请求
   */
  async post<T>(url: string, data?: any, options?: { timeout?: number }): Promise<T> {
    const response = await this.instance.post<ApiResponse<T>>(url, data, options);
    return response.data.data!;
  }

  /**
   * PUT 请求
   */
  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.instance.put<ApiResponse<T>>(url, data);
    return response.data.data!;
  }

  /**
   * PATCH 请求
   */
  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.instance.patch<ApiResponse<T>>(url, data);
    return response.data.data!;
  }

  /**
   * DELETE 请求
   */
  async delete<T>(url: string): Promise<T> {
    const response = await this.instance.delete<ApiResponse<T>>(url);
    return response.data.data!;
  }
}

// 导出单例实例
export const httpClient = new HttpClient();
